import { type FileWithStatus, FileStatus } from '@ingenico/react-ui/file-handlerV2';
import { toArray, toString } from 'lodash-es';

import type { TFunction } from 'i18next';
import type { FieldError } from 'react-hook-form';
import type {
  AppVersionApplicationFile,
  AppVersionDownloadPolicy,
} from '../../pages/applications-version-details-page/applicationVersionDetails.model';
import type {
  ParameterTemplate,
  AppVersionApplicationFile as SysPackAppVersionApplicationFile,
} from '../../pages/create-system-package-version-page/createSystemPackageVersionPage.model';
import { isArrayOfParameterTemplate } from '../../pages/create-system-package-version-page/createSystemPackageVersionPage.model';
import { getHumanizedFileSize } from '../../utils/fileSizeConversion.util';
import { getAllFilesFromSystemPackageParameterTemplate } from '../../utils/getAllFilesFromParameterTemplate.util';
import { ApplicationVersionFormError } from '../application-version-details-steps/applicationVersionDetailsSteps.model';
import type { DeviceAppVersionFilesListItem } from '../device-app-version-files-list/deviceAppVersionFilesList.model';
import type { SystemPackageVersionAutocompleteFieldValue } from '../system-package-version-fields/systemPackageVersionFields.model';
import { SystemPackageVersionFormError } from '../system-package-version-steps/systemPackageVersionSteps.model';
import type {
  ApplicationVersionAutocompleteFieldValue,
  DisplayedAppVersionDownloadPolicy,
} from './applicationVersionFields.model';

export const allowedAndRequiredTargetLocationExtensionsForBuild = [
  '.par',
  '.png',
  '.txt',
  '.csv',
  '.jpg',
  '.json',
  '.ini',
  '.zip',
];
export const allowedAndNonRequiredTargetLocationExtensionsForBuild = ['.apk'];

export const allowedSystemPackageSpecificExtensions = ['.uns'];

export const allowedApplicationFilesExtensions = [
  ...allowedAndNonRequiredTargetLocationExtensionsForBuild,
  ...allowedAndRequiredTargetLocationExtensionsForBuild,
];

export const allowedSystemPackageFilesExtensions = [
  ...allowedAndNonRequiredTargetLocationExtensionsForBuild,
  ...allowedAndRequiredTargetLocationExtensionsForBuild,
  ...allowedSystemPackageSpecificExtensions,
];

export const allowedParameterTemplatesFilesExtensions = ['.xml'];

export const parameterTemplatesFilesMaxFileSize = 500;

export const parameterTemplatesFilesUnit = 'KB';

export const applicationFilesMaxFileSizeInMB = 500;

export const applicationFilesUnit = 'MB';

export const developerFileMaxFileSizeInMB = 100;

export const parameterTemplatesMaxFileSize = 0.5;

export const parameterTemplatesSystemPackageMaxFileSize = 1;

export function verifySelectFieldIsChecked(
  { value }: ApplicationVersionAutocompleteFieldValue,
  fieldValues?: ApplicationVersionAutocompleteFieldValue[],
): boolean {
  return fieldValues ? getSelectedFieldValues(fieldValues).includes(value) : false;
}

export function buildExistingDeviceApplicationFilesList(
  t: TFunction,
  fieldValue?: ParameterTemplate[],
): DeviceAppVersionFilesListItem[] {
  const filesArray = getAllFilesFromSystemPackageParameterTemplate(fieldValue);

  return toArray(
    filesArray?.map(({ name, size }) => ({
      filename: name,
      fileInfo: getHumanizedFileSize(size, t),
    })),
  );
}

export function getSelectedFieldValues(fieldValues?: ApplicationVersionAutocompleteFieldValue[]): string[] {
  return toArray(fieldValues?.map(({ value: selectedValue }) => selectedValue));
}

export function sumSecondaryFilesSize(
  allFiles?: AppVersionApplicationFile[],
  selectedFiles?: ApplicationVersionAutocompleteFieldValue[],
): number {
  const existingSizePerFile =
    allFiles?.reduce((accum, { id = '', size }) => ({ ...accum, [id]: size }), {} as Record<string, number>) ?? {};
  return selectedFiles?.reduce((accum, { value }) => accum + (existingSizePerFile[value] ?? 0), 0) ?? 0;
}

export function getSumUploadedFiles(
  files: AppVersionApplicationFile[],
  t: TFunction,
  existingFiles?: ApplicationVersionAutocompleteFieldValue[],
  allExistingFiles?: AppVersionApplicationFile[],
): string {
  const sumInBytes = files.reduce((accum, { size }) => accum + size, 0);
  return getHumanizedFileSize(sumInBytes + sumSecondaryFilesSize(allExistingFiles, existingFiles), t);
}

export function verifyTemplatesFilesWereUploaded(fieldValue?: AppVersionApplicationFile[], index?: number): boolean {
  return index === undefined ? false : !!fieldValue?.length;
}

export function getSumUploadedTemplates(
  parameterTemplates: ParameterTemplate[],
  t: TFunction,
  existingTemplates?: ApplicationVersionAutocompleteFieldValue[],
  allExistingTemplates?: AppVersionApplicationFile[],
): string {
  const sum = parameterTemplates?.reduce(
    (accum, { template }) => accum + template.reduce((accumTemplatesSize, { size }) => accumTemplatesSize + size, 0),
    0,
  );
  return getHumanizedFileSize(sum + sumSecondaryFilesSize(allExistingTemplates, existingTemplates), t);
}

// This method only exist because the status of the formulary suddendly changes types
export function getSumOfFileSizes(
  files: AppVersionApplicationFile[] | ParameterTemplate[],
  t: TFunction,
  existingFiles?: ApplicationVersionAutocompleteFieldValue[],
  allExistingFiles?: AppVersionApplicationFile[],
): string {
  if (isArrayOfParameterTemplate(files)) {
    return getSumUploadedTemplates(files, t, existingFiles, allExistingFiles);
  }
  return getSumUploadedFiles(files, t, existingFiles, allExistingFiles);
}

export function createUploadedFileList(uploadedFiles: AppVersionApplicationFile[]): FileWithStatus[] {
  return uploadedFiles?.map(({ file, name, size, error, messageError, checksum, parsedTemplateId }, index) => ({
    file: file ?? null,
    name,
    size,
    status: error ? FileStatus.REJECTED : FileStatus.UPLOADED,
    hash: checksum,
    index,
    error: error && messageError ? messageError : undefined,
    parsedTemplateId,
  }));
}

export function transformApplicationFileListToSelect(
  files?: AppVersionApplicationFile[],
): ApplicationVersionAutocompleteFieldValue[] {
  return toArray(files?.map(({ name, id, targetLocation }) => ({ label: name, value: toString(id), targetLocation })));
}
export function transformSystemPackageApplicationFileListToSelect(
  files?: SysPackAppVersionApplicationFile[],
): SystemPackageVersionAutocompleteFieldValue[] {
  return toArray(files?.map(({ name, id, targetLocation }) => ({ label: name, value: toString(id), targetLocation })));
}

export function getDeploymentRulesFieldError({
  type,
  applicationFilesError,
  existingFilesError,
  downloadPolicy,
  id,
}: {
  type: 'existingAppFiles' | 'applicationFiles';
  existingFilesError?: FieldError;
  applicationFilesError?: FieldError;
  downloadPolicy?: AppVersionDownloadPolicy[];
  id: string;
}): DisplayedAppVersionDownloadPolicy[] | undefined {
  if (
    type === 'existingAppFiles' &&
    [
      ApplicationVersionFormError.NoEmptyNamesInDeploymentRules,
      SystemPackageVersionFormError.NoEmptyNamesInDeploymentRules,
    ].includes(existingFilesError?.type as ApplicationVersionFormError | SystemPackageVersionFormError)
  ) {
    let error = null;
    try {
      error = JSON.parse(existingFilesError?.message ?? null);
    } catch {
      error = null;
    }
    const fieldsWithError = existingFilesError?.message ? (error as Record<string, Record<string, string>>)?.[id] : {};
    return downloadPolicy?.map((policy, index) =>
      fieldsWithError?.[toString(index)]
        ? { ...policy, error: true, helperText: fieldsWithError?.[toString(index)] }
        : policy,
    );
  } else if (
    type === 'applicationFiles' &&
    [
      ApplicationVersionFormError.NoEmptyNamesInDeploymentRules,
      SystemPackageVersionFormError.NoEmptyNamesInDeploymentRules,
    ].includes(applicationFilesError?.type as ApplicationVersionFormError | SystemPackageVersionFormError)
  ) {
    let error = null;
    try {
      error = JSON.parse(applicationFilesError?.message ?? null);
    } catch {
      error = null;
    }
    const fieldsWithError = applicationFilesError?.message
      ? (error as Record<string, Record<string, string>>)?.[id]
      : {};
    return downloadPolicy?.map((policy, index) =>
      fieldsWithError?.[toString(index)]
        ? { ...policy, error: true, helperText: fieldsWithError?.[toString(index)] }
        : policy,
    );
  } else {
    return downloadPolicy;
  }
}
